import { useState, useEffect, useRef, useMemo } from "react"
import SelectField from '../common/SelectField.js';
import { useParams, useNavigate, Link } from "react-router-dom"
import { mlSheetService } from "../../services/mlSheetService"
import { useAuth } from "../../context/AuthContext"
import { DashboardLayout } from '../common/page_component/index.js'
import Loader from "../common/Loader"
import Alert from "../common/Alert"
import { ArrowLeft, Pencil, Download, ArrowRight, Search, X, QrCode, Package, Printer, Eye } from "lucide-react";
import Button from '../common/Button.js';
import RowActionsMenu from '../common/RowActionsMenu.js';
import MlAccessAgentChips from './MlAccessAgentChips.js';
import SettlementModalShell from '../common/SettlementModalShell.js';
import SettlementFieldList from '../common/SettlementFieldList.js';
import AgentCommissionLedger from '../common/AgentCommissionLedger.js';
import { buildAgentMlModalLedger } from '../../utils/agentMlCollectionDisplay.js';
import { shipmentMatchesSearch } from "../../utils/mlSheetShipmentSearch.js"
import { isShipmentDeleted, shipmentDeletedRowClass } from "../../utils/shipmentDeleted.js"
import DeletedShipmentInfoModal from "../shipment/DeletedShipmentInfoModal.js"
import { isContractPartyAdminRole, isUserOnMlSheetAccessList, hideMlSheetAmounts, canDownloadMlSheetPdf, isAssignedMlAccessViewer } from "../../utils/mlSheetAgentAccess.js"
import { formatRouteLabel } from "../../utils/routeDisplay.js"
import { formatCurrencyPaise } from "../../utils/formatCurrency.js"
import { canPrintShipmentQr, printShipmentQrCode, downloadShipmentQrCodesBulk } from "../../utils/shipmentQrPrint.js"
import { downloadPdfBlob, printPdfBlob, parseBlobErrorMessage } from "../../utils/printPdfBlob.js"
import {
  downloadMlSheetInvoices,
  downloadMlSheetReceipts,
  printMlSheetInvoices,
  printMlSheetReceipts,
} from "../../utils/mlSheetBulkDocuments.js"
import { usesLabourKeepCommissionRemit } from "../../utils/networkMode.js"
import { useTranslation } from 'react-i18next';

const MOBILE_STEPS = [
  { id: "summary", label: "Summary" },
  { id: "details", label: "Details" },
  { id: "shipments", label: "Shipments" },
]

const PAYMENT_MODE_OPTIONS = [
  { value: "all", label: "All Payments" },
  { value: "paid", label: "Paid" },
  { value: "to_pay", label: "To Pay" },
  { value: "credit", label: "Credit" },
]

const ViewMLSheet = () => {
  const { t } = useTranslation();
  const { mlSheetId } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const labourKeepRemit = usesLabourKeepCommissionRemit(user)
  const [mlSheet, setMLSheet] = useState(null)
  const [shipments, setShipments] = useState([])
  const [shipmentsDetailLocked, setShipmentsDetailLocked] = useState(false)
  const [loading, setLoading] = useState(true)
  const [downloadingPDF, setDownloadingPDF] = useState(false)
  const [printingPDF, setPrintingPDF] = useState(false)
  const [printingEwaySheet, setPrintingEwaySheet] = useState(false)
  const [downloadingShipmentQr, setDownloadingShipmentQr] = useState(false)
  const [bulkAction, setBulkAction] = useState("")
  const [docActionMessage, setDocActionMessage] = useState('')
  const [paymentModeFilter, setPaymentModeFilter] = useState("all")
  const [shipmentSearch, setShipmentSearch] = useState("")
  const [activeDropdown, setActiveDropdown] = useState(null)
  const [headerMenuOpen, setHeaderMenuOpen] = useState(false)
  const [mobileStep, setMobileStep] = useState(0)
  const [accessDetail, setAccessDetail] = useState(null)
  const [deletedInfoShipment, setDeletedInfoShipment] = useState(null)
  const [isMobileLayout, setIsMobileLayout] = useState(() =>
    typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches
  )
  const dropdownRef = useRef(null)
  const headerMenuRef = useRef(null)

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)")
    const handler = (e) => setIsMobileLayout(e.matches)
    mq.addEventListener("change", handler)
    return () => mq.removeEventListener("change", handler)
  }, [])

  const mobileStepPanelClass = (step) => {
    if (!isMobileLayout) return ""
    if (mobileStep !== step) return "hidden"
    return step === MOBILE_STEPS.length - 1
      ? "flex flex-col flex-1 min-h-0 overflow-hidden pb-4"
      : "flex flex-col flex-1 min-h-0 overflow-y-auto"
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const paymentModeSelect = (className = "min-w-[9.5rem] w-auto max-w-[11rem] shrink-0") => (
    <SelectField
      variant="filter"
      portal
      value={paymentModeFilter}
      onChange={(e) => setPaymentModeFilter(e.target.value)}
      options={PAYMENT_MODE_OPTIONS}
      className={className}
    />
  )

  const filteredShipments = useMemo(
    () => shipments.filter((s) => shipmentMatchesSearch(s, shipmentSearch)),
    [shipments, shipmentSearch],
  )

  useEffect(() => {
    fetchMLSheet()
  }, [mlSheetId])

  const fetchMLSheet = async () => {
    setLoading(true)
    try {
      const rowsRes = await mlSheetService.getMLSheetViewRows(mlSheetId)
      const sheetData = rowsRes.data.ml_sheet || rowsRes.data.data || {}
      const shipmentsData = rowsRes.data.shipments || []
      const summaryData = rowsRes.data.summary || {}
      setMLSheet({
        ...sheetData,
        summary: summaryData,
        outstanding_to_pay: summaryData.total_balance,
      })
      setShipments(Array.isArray(shipmentsData) ? shipmentsData : [])
      setLoading(false)

      try {
        const metaRes = await mlSheetService.getMLSheet(mlSheetId, { omit_shipments: 1 })
        const metaSheet = metaRes.data.ml_sheet || metaRes.data.data || {}
        const metaSummary = metaRes.data.summary || {}
        setMLSheet((prev) => ({
          ...(prev || {}),
          ...metaSheet,
          summary: {
            ...(prev?.summary || {}),
            ...Object.fromEntries(
              Object.entries(metaSummary).filter(([, value]) => value != null),
            ),
          },
          hide_amounts: Boolean(metaRes.data.hide_amounts),
          can_download_pdf: metaRes.data.can_download_pdf,
          can_update_status: metaRes.data.can_update_status,
          is_editable: metaRes.data.is_editable,
          edit_blockers: metaRes.data.edit_blockers,
          outstanding_to_pay: prev?.summary?.total_balance ?? metaSummary.total_balance,
        }))
        setShipmentsDetailLocked(Boolean(metaRes.data.shipments_detail_locked))
      } catch (metaError) {
        console.error("Error fetching ML sheet details:", metaError)
      }
    } catch (error) {
      console.error("Error fetching ML Sheet:", error)
      Alert.error("Failed to load ML Sheet")
      navigate("/ml-sheets")
    } finally {
      setLoading(false)
    }
  }

  // const canViewShipment = () => {
  //   if (!user) return false
  //   const allowedRoles = ["System Admin", "Admin", "Accountant"]
  //   return allowedRoles.includes(user.role)
  // }

  const handleGeneratePDF = async () => {
    try {
      setDownloadingPDF(true)
      setDocActionMessage('Preparing ML sheet...')
      const response = await mlSheetService.generatePDF(mlSheetId, paymentModeFilter)
      await downloadPdfBlob(response.data, `ml-sheet-${mlSheetId}.pdf`)
      Alert.success("PDF downloaded successfully")
    } catch (error) {
      console.error("Error generating PDF:", error)
      Alert.error(await parseBlobErrorMessage(error, "Failed to generate PDF"))
    } finally {
      setDownloadingPDF(false)
      setDocActionMessage('')
    }
  }

  const handlePrintEwaySheet = async () => {
    try {
      setPrintingEwaySheet(true)
      setDocActionMessage('Preparing e-Way Bills...')
      const response = await mlSheetService.ewayBillSheet(mlSheetId)
      const result = await printPdfBlob(response.data, `eway_bills_${mlSheetId}.pdf`, { allowDownloadFallback: true })
      if (result.method === 'download') {
        Alert.success('Popup blocked — e-Way Bill PDF downloaded. Open the file to print.')
      } else if (result.method === 'blocked') {
        Alert.error('Could not open the print dialog')
      }
    } catch (error) {
      Alert.error(await parseBlobErrorMessage(error, 'Failed to print e-Way Bills'))
    } finally {
      setPrintingEwaySheet(false)
      setDocActionMessage('')
    }
  }

  const handlePrintPDF = async () => {
    try {
      setPrintingPDF(true)
      setDocActionMessage('Preparing ML sheet...')
      const response = await mlSheetService.generatePDF(mlSheetId, paymentModeFilter)
      const result = await printPdfBlob(response.data, `ml-sheet-${mlSheetId}.pdf`)
      if (result.method === 'blocked') {
        Alert.error('Could not open the print dialog')
      }
    } catch (error) {
      console.error("Error printing PDF:", error)
      Alert.error(await parseBlobErrorMessage(error, "Failed to print PDF"))
    } finally {
      setPrintingPDF(false)
      setDocActionMessage('')
    }
  }

  const getSheetShipmentIds = () => {
    const fromShipments = shipments.map((s) => s.shipment_id).filter(Boolean)
    if (fromShipments.length) return fromShipments
    return (mlSheet?.shipment_ids || []).filter(Boolean)
  }

  const runBulkDocuments = async (action, busyKey, fallback) => {
    if (!mlSheetId || bulkAction) return
    try {
      setBulkAction(busyKey)
      await action(mlSheetId, paymentModeFilter)
    } catch (error) {
      Alert.error(await parseBlobErrorMessage(error, fallback))
    } finally {
      setBulkAction("")
    }
  }

  const buildDocumentMenuItems = () => {
    if (!getSheetShipmentIds().length) return []
    return [
      {
        key: "inv-dl",
        label: bulkAction === "inv-dl" ? "Downloading..." : "Download Invoices",
        icon: "🧾",
        onClick: () => runBulkDocuments(downloadMlSheetInvoices, "inv-dl", "Failed to download invoices"),
      },
      {
        key: "inv-pr",
        label: bulkAction === "inv-pr" ? "Printing..." : "Print Invoices",
        icon: "🖨️",
        onClick: () => runBulkDocuments(printMlSheetInvoices, "inv-pr", "Failed to print invoices"),
      },
      {
        key: "rc-dl",
        label: bulkAction === "rc-dl" ? "Downloading..." : "Download Receipts",
        icon: "📄",
        onClick: () => runBulkDocuments(downloadMlSheetReceipts, "rc-dl", "Failed to download receipts"),
      },
      {
        key: "rc-pr",
        label: bulkAction === "rc-pr" ? "Printing..." : "Print Receipts",
        icon: "🖨️",
        onClick: () => runBulkDocuments(printMlSheetReceipts, "rc-pr", "Failed to print receipts"),
      },
    ]
  }

  const renderDocumentMenu = (className = "relative menu-container shrink-0") => {
    if (!getSheetShipmentIds().length) return null
    return (
      <RowActionsMenu
        className={className}
        isOpen={headerMenuOpen}
        onToggle={() => setHeaderMenuOpen((open) => !open)}
        onClose={() => setHeaderMenuOpen(false)}
        menuRef={headerMenuRef}
        extraItems={buildDocumentMenuItems()}
        menuClassName="w-52 bg-white rounded-sm shadow-lg border border-gray-200"
      />
    )
  }

  const handleDownloadAllShipmentQR = async () => {
    try {
      setDownloadingShipmentQr(true)
      setDocActionMessage('Preparing QR codes...')
      await downloadShipmentQrCodesBulk(
        getSheetShipmentIds(),
        `shipment_qr_codes_${mlSheetId}.pdf`,
      )
    } finally {
      setDownloadingShipmentQr(false)
      setDocActionMessage('')
    }
  }

  const getStatusColor = (status) => {
    const statusUpper = (status || "").toUpperCase()
    return statusUpper === "OPEN"
      ? "bg-green-100 text-green-800 border-green-300"
      : "bg-red-100 text-red-800 border-red-300"
  }

  const getShipmentStatusColor = (status) => {
    const statusMap = {
      booked: "bg-blue-100 text-blue-800",
      dispatched: "bg-purple-100 text-purple-800",
      delivered: "bg-green-100 text-green-800",
      cancelled: "bg-red-100 text-red-800",
    }
    return statusMap[status?.toLowerCase()] || "bg-gray-100 text-gray-800"
  }

  const canEdit = () => {
    if (isAssignedMlAccessViewer(mlSheet, user)) return false
    if (isContractPartyAdminRole(user)) return false
    if (mlSheet?.is_editable != null) return Boolean(mlSheet.is_editable)
    return user?.role === "System Admin" || user?.role === "Admin" || user?.role === "Accountant"
  }

  const isAssignedAgentOnSheet = () => {
    const uid = user?.user_id
    if (!uid || !mlSheet) return false
    if (mlSheet.created_by === uid) return true
    if (isUserOnMlSheetAccessList(mlSheet, uid)) return true
    return (mlSheet.agent_collections || []).some(
      (row) => String(row?.user_id || "") === String(uid),
    )
  }

  const canViewShipment = () => {
    if (shipmentsDetailLocked) return false
    if (isContractPartyAdminRole(user)) return true
    if (user?.role === "Agent") return isAssignedAgentOnSheet()
    return true
  }

  const hideAmounts = hideMlSheetAmounts(mlSheet, user)
  const partyAmountHidden = (shipment) => {
    if (!shipmentsDetailLocked) return false
    if (shipment?.party_amount_hidden) return true
    const mode = String(shipment?.mode_of_payment || shipment?.payment_mode || '').toLowerCase()
    return mode === 'paid' || mode === 'to_pay'
  }
  const canPdf = canDownloadMlSheetPdf(mlSheet, user, shipments)
  const accessLedger = accessDetail?.collection
    ? buildAgentMlModalLedger(accessDetail.collection, shipments)
    : null

  const handlePrintShipmentQR = (shipment) => {
    printShipmentQrCode(shipment.shipment_id, { onClose: () => setActiveDropdown(null) })
  }

  const buildShipmentActionItems = (shipment) => {
    if (!canPrintShipmentQr(user)) return []
    return [
      {
        label: 'Download Shipment QR',
        icon: '🔳',
        onClick: () => handlePrintShipmentQR(shipment),
      },
    ]
  }

  const openShipmentFromMl = (shipment) => {
    if (isShipmentDeleted(shipment)) {
      setDeletedInfoShipment(shipment)
      return
    }
    navigate(`/shipments/view/${shipment.shipment_id}`)
  }

  const renderShipmentActions = (shipment) => {
    const showView = canViewShipment()
    const showQr = canPrintShipmentQr(user)
    if (!showView && !showQr) {
      return <span className="text-gray-400 text-sm">—</span>
    }
    return (
      <RowActionsMenu
        isOpen={activeDropdown === shipment.shipment_id}
        onToggle={() => setActiveDropdown(activeDropdown === shipment.shipment_id ? null : shipment.shipment_id)}
        onClose={() => setActiveDropdown(null)}
        menuRef={dropdownRef}
        onView={showView ? () => openShipmentFromMl(shipment) : undefined}
        extraItems={buildShipmentActionItems(shipment)}
      />
    )
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <Loader fullScreen={false} size="default" message="Loading ML Sheet..." />
        </div>
      </DashboardLayout>
    )
  }

  if (!mlSheet) {
    return (
      <DashboardLayout>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-gray-600 text-lg font-semibold">ML Sheet not found</p>
            <button
              onClick={() => navigate("/ml-sheets")}
              className="mt-4 px-6 py-2 bg-orange-500 text-white rounded-xl hover:bg-orange-600 font-semibold"
            >
              Back to Dispatch
            </button>
          </div>
        </div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout fullWidth fillViewport={isMobileLayout}>
                {isMobileLayout && (
                  <div className="sticky top-0 z-30 bg-white shrink-0 mb-3 pb-2 border-b border-gray-100">
                    <div className="flex items-center gap-1 mb-2">
                      {MOBILE_STEPS.map((step, idx) => (
                        <button
                          key={step.id}
                          type="button"
                          onClick={() => setMobileStep(idx)}
                          className={`flex-1 min-w-0 text-center py-1.5 px-0.5 rounded-lg text-[10px] font-semibold transition truncate ${
                            idx === mobileStep
                              ? "bg-orange-500 text-white shadow-sm"
                              : idx < mobileStep
                                ? "bg-orange-100 text-orange-700"
                                : "bg-gray-100 text-gray-400"
                          }`}
                        >
                          {step.label}
                        </button>
                      ))}
                    </div>
                    <div className="flex items-center justify-between gap-2 mb-2 min-w-0">
                      <h1 className="flex items-center gap-1.5 text-sm font-bold text-secondary truncate min-w-0">
                        <Package className="w-4 h-4 text-buttonhoverTxt shrink-0" />
                        <span className="truncate">{MOBILE_STEPS[mobileStep].label}</span>
                      </h1>
                      <span className="text-[10px] text-gray-500 shrink-0 truncate max-w-[40%]">
                        #{mlSheet.ml_sheet_id}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 flex-nowrap overflow-x-auto pb-0.5 -mx-0.5 px-0.5 overflow-visible">
                      {canPdf && (
                      <div className="shrink-0 min-w-[8.5rem] max-w-[10rem] relative z-40">
                        {paymentModeSelect("w-auto min-w-[8.5rem] max-w-[10rem] shrink-0")}
                      </div>
                      )}
                      {canPdf && (
                      <Button
                        variant="secondary"
                        type="button"
                        icon={Download}
                        mobileIconOnly
                        mobileLabel="Download Dispatch"
                        onClick={handleGeneratePDF}
                        disabled={downloadingPDF}
                        loading={downloadingPDF}
                      />
                      )}
                      {canPdf && (
                      <Button
                        variant="secondary"
                        type="button"
                        icon={Printer}
                        mobileIconOnly
                        mobileLabel="Print Dispatch"
                        onClick={handlePrintPDF}
                        disabled={printingPDF}
                        loading={printingPDF}
                      />
                      )}
                      <Button
                        variant="secondary"
                        type="button"
                        icon={Printer}
                        mobileIconOnly
                        mobileLabel="E-Way Bills"
                        onClick={handlePrintEwaySheet}
                        disabled={printingEwaySheet}
                        loading={printingEwaySheet}
                      />
                      {canPrintShipmentQr(user) && (
                        <Button
                          variant="secondary"
                          type="button"
                          icon={Download}
                          mobileIconOnly
                          mobileLabel="Download Shipment QR"
                          onClick={handleDownloadAllShipmentQR}
                          disabled={downloadingShipmentQr}
                          loading={downloadingShipmentQr}
                        />
                      )}
                      {renderDocumentMenu()}
                      {canEdit() && (
                        <Button
                          variant="secondary"
                          type="button"
                          icon={Pencil}
                          mobileIconOnly
                          mobileLabel="Edit"
                          onClick={() => navigate(`/ml-sheets/edit/${mlSheet.ml_sheet_id}`)}
                        />
                      )}
                      <Button
                        variant="secondary"
                        type="button"
                        icon={ArrowLeft}
                        mobileIconOnly
                        mobileLabel="Back to Dispatch"
                        onClick={() => navigate("/ml-sheets")}
                      />
                    </div>
                    {mlSheet.is_editable === false && mlSheet.edit_blockers?.length > 0 && (
                      <p className="text-[10px] text-amber-700 bg-amber-50 px-2 py-1 rounded border border-amber-200 mt-2">
                        {mlSheet.edit_blockers[0].message}
                      </p>
                    )}
                  </div>
                )}

                {/* Desktop / iPad header */}
                {!isMobileLayout && (
                <div className="shrink-0 mb-4 sm:mb-6">
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div className="min-w-0 flex-1 text-secondary">
                      <h1 className="text-xl sm:text-2xl font-bold text-secondary flex items-start gap-2 sm:gap-3">
                        <Package className="w-5 h-5 sm:w-6 sm:h-6 text-buttonhoverTxt shrink-0 mt-0.5" />
                        <span className="min-w-0 break-words leading-snug">
                          {t('pages.mlSheet.view')} #{mlSheet.ml_sheet_id}
                        </span>
                      </h1>
                      <p className="text-gray-700 text-xs sm:text-sm mt-1">
                        Created on{" "}
                        {new Date(mlSheet.created_at).toLocaleDateString("en-IN", {
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                        })}
                      </p>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 w-full lg:w-auto lg:justify-end shrink-0">
                      {canPdf && paymentModeSelect("min-w-[9.5rem] w-auto max-w-[11rem] shrink-0")}

                      {canPdf && (
                      <Button variant="primary" onClick={handleGeneratePDF} disabled={downloadingPDF} loading={downloadingPDF} icon={Download} className="shrink-0">
                        {downloadingPDF ? "Downloading..." : "Download Dispatch"}
                      </Button>
                      )}
                      {canPdf && (
                      <Button variant="primary" onClick={handlePrintPDF} disabled={printingPDF} loading={printingPDF} icon={Printer} className="shrink-0">
                        {printingPDF ? t("common.loading") : "Print Dispatch"}
                      </Button>
                      )}

                      {canPrintShipmentQr(user) && (
                        <Button
                          variant="primary"
                          onClick={handleDownloadAllShipmentQR}
                          disabled={downloadingShipmentQr}
                          loading={downloadingShipmentQr}
                          icon={QrCode}
                          className="shrink-0"
                        >
                          {downloadingShipmentQr ? "Downloading..." : "Download Shipment QR"}
                        </Button>
                      )}

                      {renderDocumentMenu()}

                      {canEdit() && (
                        <Button variant="secondary" onClick={() => navigate(`/ml-sheets/edit/${mlSheet.ml_sheet_id}`)} icon={Pencil} className="shrink-0">
                          Edit
                        </Button>
                      )}
                      {mlSheet.is_editable === false && mlSheet.edit_blockers?.length > 0 && (
                        <span className="text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded border border-amber-200 self-center shrink-0 whitespace-nowrap max-w-full">
                          {mlSheet.edit_blockers[0].message}
                        </span>
                      )}
                      <Button variant="secondary" onClick={() => navigate("/ml-sheets")} icon={ArrowLeft} className="shrink-0">
                        Back
                      </Button>
                    </div>
                  </div>
                </div>
                )}

                <div className={`${isMobileLayout ? "flex flex-col flex-1 min-h-0 overflow-hidden" : "space-y-3 sm:space-y-6"}`}>

                {/* Status and Key Info Cards */}
                <div className={`grid grid-cols-2 md:grid-cols-2 ${(hideAmounts || shipmentsDetailLocked) ? "lg:grid-cols-3" : "lg:grid-cols-5"} gap-2 sm:gap-4 ${mobileStepPanelClass(0)} md:!grid`}>
                  <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-orange-500">
                    <div className="text-secondary text-sm font-semibold mb-1">Status</div>
                    <div
                      className={`text-sm sm:text-sm text-secondary font-regular break-all`}
                    >
                      {(mlSheet.status || "").toUpperCase()}
                    </div>
                  </div>
                  <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-blue-500">
                    <div className="text-secondary text-sm font-semibold mb-1">Total Shipments</div>
                    <div className="text-sm sm:text-sm text-secondary font-regular break-all">
                      {mlSheet.summary?.total_shipments || shipments.length || 0}
                    </div>
                  </div>
                  <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-green-500">
                    <div className="text-secondary text-sm font-semibold mb-1">Total Packages</div>
                    <div className="text-sm sm:text-base text-secondary font-regular break-all">
                      {mlSheet.summary?.total_packages || shipments.reduce((sum, s) => sum + (s.no_of_articles || 0), 0)}
                    </div>
                  </div>
                  {!hideAmounts && !shipmentsDetailLocked && (
                  <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-purple-500">
                    <div className="text-secondary text-sm font-semibold mb-1">Total Amount</div>
                    <div className="text-sm sm:text-base text-secondary font-regular break-all">
                      ₹
                      {(
                        mlSheet.summary?.total_amount || shipments.reduce((sum, s) => sum + (s.total_amount || 0), 0)
                      ).toFixed(2)}
                    </div>
                  </div>
                  )}
                  {/* Additional Summary Card for Balance */}
                  {!hideAmounts && !shipmentsDetailLocked && mlSheet.summary?.total_balance > 0 && (
                    <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-gray-500">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-secondary text-sm font-semibold mb-1">Total Balance Due</div>
                          <div className="text-base font-regular text-secondary">₹{mlSheet.summary.total_balance.toFixed(2)}</div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className={`grid grid-cols-1 md:grid-cols-2 gap-2 sm:gap-6 ${mobileStepPanelClass(1)} md:!grid`}>

                  {/* Route Information */}
                  <div className="bg-white rounded-xl p-4 sm:p-6 mb-6 min-w-0">
                    <h3 className="text-base sm:text-sm font-semibold text-secondary mb-3 sm:mb-2 pb-2">
                      Route Information
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                      <div className="bg-gray-50 rounded-xl p-3 border border-primary min-w-0">
                        <div className="text-secondary text-xs font-semibold uppercase mb-1">
                          Route
                        </div>
                        <div className="text-sm font-regular text-secondary whitespace-normal break-words">
                          {mlSheet.route
                            ? formatRouteLabel(mlSheet.route) || mlSheet.route.route_name || mlSheet.route.name || "Custom"
                            : "Custom"}
                        </div>
                      </div>
                      <div className="bg-gray-50 rounded-xl p-3 border border-primary min-w-0">
                        <div className="text-secondary text-xs font-semibold uppercase mb-1">
                          Crossing Location Hubs
                        </div>
                        <div className="text-sm font-regular text-secondary whitespace-normal break-words">
                          {mlSheet.crossing_locations && mlSheet.crossing_locations.length > 0
                            ? mlSheet.crossing_locations
                                .map(loc => loc.location || loc.hub_name || loc.name)
                                .join(", ")
                            : "None"}
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-12 gap-4 items-center">

                    {/* FROM LOCATIONS */}
                    <div className="bg-gray-50 col-span-12 md:col-span-5 rounded-xl p-3 border border-primary">
                      <div className="text-secondary text-xs font-semibold uppercase mb-1">
                        From Location
                      </div>
                      <div className="text-sm font-regular text-secondary">
                        {mlSheet.from_locations && mlSheet.from_locations.length > 0
                          ? mlSheet.from_locations
                              .map(loc => loc.location || loc.hub_name || loc.name)
                              .join(", ")
                          : mlSheet.from_location?.location ||
                            mlSheet.from_location?.hub_name ||
                            mlSheet.from_location ||
                            "N/A"}
                      </div>
                    </div>

                    {/* ARROW */}
                    <div className="col-span-12 md:col-span-2 flex items-center justify-center">
                      <ArrowRight className="w-6 h-6 text-secondary" />
                    </div>

                    {/* TO LOCATIONS */}
                    <div className="bg-gray-50 col-span-12 md:col-span-5 rounded-xl p-3 border border-primary">
                      <div className="text-secondary text-xs font-semibold uppercase mb-1">
                        To Location
                      </div>
                      <div className="text-sm font-regular text-secondary">
                        {mlSheet.to_locations && mlSheet.to_locations.length > 0
                          ? mlSheet.to_locations
                              .map(loc => loc.location || loc.hub_name || loc.name)
                              .join(", ")
                          : mlSheet.to_location?.location ||
                            mlSheet.to_location?.hub_name ||
                            mlSheet.to_location ||
                            "N/A"}
                      </div>
                    </div>

                  </div>

                  </div>

                  {/* Vehicle & Driver Information */}
                  <div className="bg-white rounded-xl p-4 sm:p-6 mb-6 min-w-0">
                    <h3 className="text-base sm:text-sm font-semibold text-secondary mb-3 sm:mb-2 pb-2">
                      Vehicle & Driver Information
                    </h3>
                    <div className="space-y-3 sm:space-y-4">
                    <div className="grid grid-cols-2 gap-2 sm:gap-4">
                      <div className="bg-gray-50 rounded-xl p-3 py-2 border border-primary min-w-0">
                        <div className="text-gray-600 text-sm font-regular mb-1">Vehicle Number</div>
                        <div className="text-sm sm:text-sm text-secondary font-regular break-words">{mlSheet.vehicle_number || "N/A"}</div>
                      </div>
                      <div className="bg-gray-50 rounded-xl p-3 py-2 border border-primary min-w-0">
                        <div className="text-gray-600 text-sm font-regular mb-1">Driver Contact</div>
                        <div className="text-sm sm:text-base text-secondary font-regular break-all">{mlSheet.driver_contact || "N/A"}</div>
                      </div>
                      {mlSheet.driver_name ? (
                        <div className="bg-gray-50 rounded-xl p-3 py-2 border border-primary min-w-0">
                          <div className="text-gray-600 text-sm font-regular mb-1">Driver Name</div>
                          <div className="text-sm text-secondary font-regular break-words">{mlSheet.driver_name}</div>
                        </div>
                      ) : null}
                      {mlSheet.journey_date ? (
                        <div className="bg-gray-50 rounded-xl p-3 py-2 border border-primary min-w-0">
                          <div className="text-gray-600 text-sm font-regular mb-1">Date of Journey</div>
                          <div className="text-sm text-secondary font-regular">{mlSheet.journey_date}</div>
                        </div>
                      ) : null}
                      {(mlSheet.departure_time || mlSheet.arrival_time) ? (
                        <div className="bg-gray-50 rounded-xl p-3 py-2 border border-primary min-w-0 col-span-2">
                          <div className="text-gray-600 text-sm font-regular mb-1">Departure / Arrival</div>
                          <div className="text-sm text-secondary font-regular">
                            {mlSheet.departure_time || "—"} – {mlSheet.arrival_time || "—"}
                          </div>
                        </div>
                      ) : null}
                    </div>
                    <div>
                      <div className="text-gray-600 text-sm font-regular mb-2">
                        ML Access Agents
                        {Array.isArray(mlSheet.access_users) && mlSheet.access_users.length > 0
                          ? ` (${mlSheet.access_users.length})`
                          : ''}
                      </div>
                      <MlAccessAgentChips
                        users={Array.isArray(mlSheet.access_users) ? mlSheet.access_users : []}
                        emptyLabel="No users assigned"
                      />
                    </div>
                    </div>
                  </div>

                  {/* Access Users + Agent To-Pay collections (INTERNAL) — full width so cards don't collapse */}
                  <div className="bg-white rounded-xl shadow-md p-4 sm:p-6 mb-6 md:col-span-2 min-w-0">
                    {(() => {
                      const isAgentViewer = user?.role === "Agent"
                      const myUserId = String(user?.user_id || "")
                      const allAccessUsers = Array.isArray(mlSheet.access_users)
                        ? mlSheet.access_users
                        : []
                      const visibleAccessUsers = isAgentViewer
                        ? allAccessUsers.filter((accessUser) => {
                            const uid = String(
                              accessUser.user_id || accessUser.id || accessUser,
                            )
                            return uid === myUserId
                          })
                        : allAccessUsers

                      const collections = (
                        Array.isArray(mlSheet.agent_collections)
                          ? mlSheet.agent_collections
                          : []
                      ).filter((c) => {
                        if (!isAgentViewer) return true
                        return String(c?.user_id || "") === myUserId
                      })
                      const byUser = Object.fromEntries(
                        collections
                          .filter((c) => c?.user_id)
                          .map((c) => [String(c.user_id), c]),
                      )
                      const hasCollections = collections.length > 0

                      return (
                        <>
                          <h3 className="text-sm sm:text-sm font-semibold text-secondary mb-3 sm:mb-2 pb-2">
                            {isAgentViewer
                              ? "My To-Pay Collection"
                              : `Access Users (${allAccessUsers.length})`}
                          </h3>
                          {!visibleAccessUsers.length ? (
                            <p className="text-gray-500 text-sm">
                              {isAgentViewer
                                ? "You are not assigned on this ML sheet"
                                : "No access users assigned"}
                            </p>
                          ) : (
                            <div className="space-y-3 min-w-0">
                              {hasCollections && (
                                <p className="text-xs text-gray-500">
                                  {labourKeepRemit
                                    ? (isAgentViewer
                                      ? "Keep your commission and the unloading. Return the rest to the company."
                                      : "Agent keeps his commission and the unloading, and returns the rest.")
                                    : (isAgentViewer
                                      ? "Remit net To-Pay (after your commission). Commission is booked as expense when settlement is approved — company does not pay it out separately."
                                      : "Agent remits net To-Pay (To-Pay − commission). On settlement approval, commission is booked as expense (deducted from remittance), not paid out by the company.")}
                                </p>
                              )}
                              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 min-w-0">
                                {visibleAccessUsers.map((accessUser) => {
                                  const userId = String(
                                    accessUser.user_id || accessUser.id || accessUser,
                                  )
                                  const collection = byUser[userId]
                                  const ledger = collection
                                    ? buildAgentMlModalLedger(collection, shipments)
                                    : null
                                  const outstanding = Number(
                                    ledger?.returnToCompany ??
                                      collection?.amount_outstanding ??
                                      collection?.amount_due ??
                                      0,
                                  )
                                  const displayName =
                                    accessUser.name || collection?.name || `User ${userId}`
                                  const locationLabel =
                                    collection?.location_name ||
                                    accessUser.location_name ||
                                    accessUser.location ||
                                    ""

                                  const isPaid = Boolean(collection && outstanding <= 0)
                                  const amountClass = !collection
                                    ? "text-gray-400"
                                    : isPaid
                                      ? "text-emerald-600"
                                      : "text-red-600"

                                  return (
                                    <div
                                      key={userId}
                                      className="relative rounded-xl border border-gray-200 bg-slate-50/90 p-3 pr-9 min-w-0 flex flex-col gap-1"
                                    >
                                      <button
                                        type="button"
                                        onClick={() => setAccessDetail({ accessUser, collection, userId, displayName, locationLabel })}
                                        className="absolute top-2 right-2 p-1 rounded-md text-gray-500 hover:text-blue-600 hover:bg-white transition-colors"
                                        aria-label={`View ${displayName}`}
                                        title="View"
                                      >
                                        <Eye className="w-4 h-4" />
                                      </button>
                                      <p className={`text-sm font-semibold tabular-nums leading-none ${amountClass}`}>
                                        {collection
                                          ? formatCurrencyPaise(outstanding)
                                          : "₹0"}
                                      </p>
                                      <p className="font-medium text-sm text-secondary leading-snug break-words [overflow-wrap:anywhere] min-w-0">
                                        <Link
                                          to={`/users/view/${userId}`}
                                          className="hover:text-orange-600 hover:underline transition-colors"
                                        >
                                          {displayName}
                                        </Link>
                                      </p>
                                      {locationLabel ? (
                                        <p className="text-xs text-gray-500 truncate">{locationLabel}</p>
                                      ) : null}
                                      {collection ? (
                                        <p className="text-[11px] text-gray-500 leading-snug">
                                          {collection.status === "SETTLED" ? "To-Pay settled" : "To-Pay pending"}
                                          {collection.commission_status && Number(collection.commission_amount || 0) > 0
                                            ? ` · Comm. ${collection.commission_status === "PAID" ? "paid" : "unpaid"}`
                                            : ""}
                                        </p>
                                      ) : (
                                        <p className="text-[11px] text-gray-400">No To-Pay amount mapped</p>
                                      )}
                                      {(!hideAmounts && ledger?.remitBreakdown?.length) ? (
                                        <p className="text-[11px] text-gray-500 leading-snug">
                                          {[
                                            Number(ledger.unloadingReceiving || 0) > 0.009
                                              ? `Unloading ${formatCurrencyPaise(ledger.unloadingReceiving)}`
                                              : null,
                                            Number(ledger.otherRemit || 0) > 0.009
                                              ? `Other ${formatCurrencyPaise(ledger.otherRemit)}`
                                              : null,
                                            Number(ledger.toPayHeld || 0) > 0.009
                                              ? `After delivery ${formatCurrencyPaise(ledger.toPayHeld)}`
                                              : null,
                                          ].filter(Boolean).join(" · ")}
                                        </p>
                                      ) : null}
                                    </div>
                                  )
                                })}
                              </div>
                            </div>
                          )}
                        </>
                      )
                    })()}
                  </div>
                </div>

                {/* Shipments Table */}
                <div className={`bg-white rounded-xl shadow-md overflow-hidden ${mobileStepPanelClass(2)} md:!block`}>
                  {shipmentsDetailLocked && (
                    <div className="mx-4 mt-4 sm:mx-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                      Consignor, consignee, and amount stay hidden on Paid and To Pay shipments until this sheet&apos;s return is approved. Shipment id, route, packages, and status stay visible.
                    </div>
                  )}
                  <div className="bg-white px-4 sm:px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 shrink-0">
                    <h3 className="text-base sm:text-sm font-regular text-secondary">
                      Included Shipments ({filteredShipments.length}{shipmentSearch.trim() ? ` of ${shipments.length}` : ""})
                    </h3>
                    {shipments.length > 0 && (
                      <div className="relative w-full sm:max-w-xs">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          value={shipmentSearch}
                          onChange={(e) => setShipmentSearch(e.target.value)}
                          placeholder="Exact match: ID, name, contact..."
                          className="w-full pl-9 pr-9 py-2 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400"
                        />
                        {shipmentSearch && (
                          <button
                            type="button"
                            onClick={() => setShipmentSearch("")}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  <div className={isMobileLayout ? "flex-1 min-h-0 overflow-y-auto overscroll-contain" : ""}>
                  {shipments.length === 0 ? (
                    <div className="text-center py-12">
                      <p className="text-gray-500 text-lg">No shipments in this ML Sheet</p>
                    </div>
                  ) : filteredShipments.length === 0 ? (
                    <div className="text-center py-12">
                      <p className="text-gray-500 text-lg">No shipments match your search</p>
                    </div>
                  ) : (
                    <div>
                    <>
                      {/* Desktop Table */}
                      <div className="hidden md:block w-full overflow-x-auto">
                        <table className="w-full min-w-[900px] table-fixed border-collapse">
                          <colgroup>
                            <col style={{ width: "56px" }} />
                            <col style={{ width: "14%" }} />
                            <col style={{ width: "14%" }} />
                            <col style={{ width: "14%" }} />
                            <col style={{ width: "16%" }} />
                            <col style={{ width: "8%" }} />
                            {!hideAmounts ? <col style={{ width: "9%" }} /> : null}
                            <col style={{ width: "9%" }} />
                            <col style={{ width: "9%" }} />
                            <col style={{ width: "72px" }} />
                          </colgroup>
                          <thead>
                            <tr className="bg-gray-50 text-gray-800 border-b">
                              <th className="px-3 py-3 text-xs lg:text-sm font-semibold text-center whitespace-nowrap">Sr No</th>
                              <th className="px-3 py-3 text-xs lg:text-sm font-semibold text-center whitespace-nowrap">Shipment ID</th>
                              <th className="px-3 py-3 text-xs lg:text-sm font-semibold text-center">Sender</th>
                              <th className="px-3 py-3 text-xs lg:text-sm font-semibold text-center">Receiver</th>
                              <th className="px-3 py-3 text-xs lg:text-sm font-semibold text-center">Route</th>
                              <th className="px-3 py-3 text-xs lg:text-sm font-semibold text-center whitespace-nowrap">Packages</th>
                              {!hideAmounts && (
                              <th className="px-3 py-3 text-xs lg:text-sm font-semibold text-center whitespace-nowrap">Amount</th>
                              )}
                              <th className="px-3 py-3 text-xs lg:text-sm font-semibold text-center whitespace-nowrap">Balance</th>
                              <th className="px-3 py-3 text-xs lg:text-sm font-semibold text-center whitespace-nowrap">Status</th>
                              <th className="px-2 py-3 text-xs lg:text-sm font-semibold text-center whitespace-nowrap">Action</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {filteredShipments.map((shipment, index) => (
                              <tr key={shipment.id} className={shipmentDeletedRowClass(shipment, `hover:bg-gray-50 transition-colors ${index % 2 === 0 ? "bg-white" : "bg-gray-50"}`)}>
                                <td className="px-3 py-3 text-sm font-medium text-gray-800 text-center align-middle">{index + 1}</td>
                                <td className="px-3 py-3 text-center text-sm align-middle break-all">
                                  {canViewShipment() ? (
                                    <button
                                      type="button"
                                      onClick={() => openShipmentFromMl(shipment)}
                                      className="font-semibold text-orange-600 hover:underline"
                                    >
                                      {shipment.shipment_id}
                                    </button>
                                  ) : (
                                    <span className="font-semibold text-gray-900">{shipment.shipment_id}</span>
                                  )}
                                </td>
                                <td className="px-3 py-3 text-center text-sm align-middle">
                                  <div className="font-semibold text-gray-900 break-words">{partyAmountHidden(shipment) ? '—' : shipment.sender_name}</div>
                                  <div className="text-xs text-gray-600">{partyAmountHidden(shipment) ? '' : shipment.sender_contact}</div>
                                </td>
                                <td className="px-3 py-3 text-center text-sm align-middle">
                                  <div className="font-semibold text-gray-900 break-words">{partyAmountHidden(shipment) ? '—' : shipment.receiver_name}</div>
                                  <div className="text-xs text-gray-600">{partyAmountHidden(shipment) ? '' : shipment.receiver_contact}</div>
                                </td>
                                <td className="px-3 py-3 text-sm text-center align-middle">
                                  <div className="font-medium text-gray-800 break-words leading-snug">
                                    <span>{shipment.sender_location || "N/A"}</span>
                                    {" "}
                                    <span className="text-secondary font-semibold">→</span>
                                    {" "}
                                    <span>{shipment.receiver_location || "N/A"}</span>
                                  </div>
                                </td>
                                <td className="px-3 py-3 text-center text-sm align-middle">
                                  <span className="inline-block text-gray-900 font-semibold text-sm">
                                    {shipment.no_of_articles}
                                  </span>
                                </td>
                                {!hideAmounts && (
                                <td className="px-3 py-3 font-semibold text-gray-900 text-center text-sm align-middle whitespace-nowrap">{partyAmountHidden(shipment) || shipment.total_amount == null ? '—' : `₹${shipment.total_amount?.toFixed(2)}`}</td>
                                )}
                                <td className="px-3 py-3 text-center text-sm align-middle whitespace-nowrap">
                                  {partyAmountHidden(shipment) || shipment.balance_amount == null ? (
                                    <span className="font-semibold text-gray-400">—</span>
                                  ) : shipment.balance_amount > 0 ? (
                                    <span className="font-semibold text-amber-700">₹{shipment.balance_amount?.toFixed(2)}</span>
                                  ) : (
                                    <span className="font-semibold text-green-700">Paid</span>
                                  )}
                                </td>
                                <td className="px-3 py-3 text-center text-sm align-middle">
                                  <span
                                    className={`px-2 py-1 rounded-full text-xs font-semibold ${getShipmentStatusColor(shipment.status)}`}
                                  >
                                    {shipment.status}
                                  </span>
                                </td>
                                <td className="px-2 py-3 text-center text-sm align-middle">
                                  {renderShipmentActions(shipment)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Mobile Cards */}
                      <div className="md:hidden divide-y divide-gray-200">
                        {filteredShipments.map((shipment, index) => (
                          <div key={shipment.id} className={shipmentDeletedRowClass(shipment, "p-4 hover:bg-orange-50 transition-colors")}>
                            <div className="flex justify-between items-start gap-2 mb-3">
                              <div className="min-w-0">
                                <div className="text-xs text-gray-500 font-semibold">#{index + 1}</div>
                                {canViewShipment() ? (
                                  <button
                                    type="button"
                                    onClick={() => openShipmentFromMl(shipment)}
                                    className="font-bold text-orange-600 text-base sm:text-lg text-left hover:underline break-all"
                                  >
                                    {shipment.shipment_id}
                                  </button>
                                ) : (
                                  <div className="font-bold text-orange-600 text-base sm:text-lg break-all">{shipment.shipment_id}</div>
                                )}
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                <span
                                  className={`px-2 py-1 rounded-full text-xs font-bold whitespace-nowrap ${getShipmentStatusColor(shipment.status)}`}
                                >
                                  {shipment.status}
                                </span>
                                {renderShipmentActions(shipment)}
                              </div>
                            </div>

                            <div className="grid grid-cols-2 gap-2 text-xs sm:text-sm mb-3">
                              <div>
                                <span className="text-gray-500">Sender:</span>
                                <p className="font-semibold text-gray-800 truncate">{partyAmountHidden(shipment) ? '—' : shipment.sender_name}</p>
                              </div>
                              <div>
                                <span className="text-gray-500">Receiver:</span>
                                <p className="font-semibold text-gray-800 truncate">{partyAmountHidden(shipment) ? '—' : shipment.receiver_name}</p>
                              </div>
                              <div>
                                <span className="text-gray-500">Packages:</span>
                                <p className="font-bold text-gray-800">{shipment.no_of_articles}</p>
                              </div>
                              {!hideAmounts && (
                              <div>
                                <span className="text-gray-500">Amount:</span>
                                <p className="font-bold text-gray-800">{partyAmountHidden(shipment) || shipment.total_amount == null ? '—' : `₹${shipment.total_amount?.toFixed(2)}`}</p>
                              </div>
                              )}
                              <div className="col-span-2">
                                <span className="text-gray-500">Balance:</span>
                                <p className={`font-bold ${!partyAmountHidden(shipment) && shipment.balance_amount > 0 ? "text-red-600" : "text-green-600"}`}>
                                  {partyAmountHidden(shipment) || shipment.balance_amount == null
                                    ? '—'
                                    : (shipment.balance_amount > 0 ? `₹${shipment.balance_amount?.toFixed(2)}` : "Paid")}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                    </div>
                  )}
                  </div>
                </div>
                </div>
    {accessDetail && (
      <SettlementModalShell
        size="sm"
        title={accessDetail.displayName}
        subtitle={[accessDetail.locationLabel, mlSheet.ml_sheet_id].filter(Boolean).join(" · ")}
        onClose={() => setAccessDetail(null)}
        footer={(
          <div className="flex justify-end">
            <Button variant="secondary" onClick={() => setAccessDetail(null)}>Close</Button>
          </div>
        )}
      >
        <div className="space-y-3">
          <SettlementFieldList
            items={[
              { label: "Agent", value: accessDetail.displayName },
              { label: "Location", value: accessDetail.locationLabel || "—" },
              {
                label: "To-Pay status",
                value: accessDetail.collection?.status === "SETTLED" ? "Settled" : (accessDetail.collection ? "Pending" : "—"),
              },
              {
                label: "Commission",
                value: accessDetail.collection?.commission_status
                  ? (accessDetail.collection.commission_status === "PAID" ? "Paid" : "Unpaid")
                  : "—",
              },
            ]}
          />
          {accessDetail.collection ? (
            <AgentCommissionLedger
              gcTotal={accessLedger?.gcTotal}
              totalFreight={accessLedger?.freight ?? accessDetail.collection.total_freight}
              freightLabel="Freight (excl. GST)"
              toPayFace={accessLedger?.showGcBreakdown ? accessLedger.toPayFace : undefined}
              toPay={accessLedger?.showGcBreakdown ? undefined : (accessDetail.collection.to_pay_freight ?? accessDetail.collection.gross_to_pay)}
              toPayHeld={accessLedger?.toPayHeld}
              commissionAmount={accessDetail.collection.commission_amount}
              commissionPct={accessDetail.collection.commission_pct}
              commissionStatus={accessDetail.collection.commission_status}
              commissionSeparate={
                Boolean(accessLedger?.commissionSeparate)
                || accessDetail.collection.deduct_commission_from_remit === false
                || labourKeepRemit
              }
              tdsAmount={accessDetail.collection.tds_amount}
              tdsPct={accessDetail.collection.tds_pct}
              netToAgent={accessDetail.collection.net_amount}
              returnToCompany={accessLedger?.returnToCompany}
              remitBreakdown={accessLedger?.remitBreakdown}
              netDue={accessLedger?.dueNow ?? accessDetail.collection.amount_due}
              netDueLabel={accessLedger?.dueLabel}
              outstanding={accessLedger?.outstanding ?? accessDetail.collection.amount_outstanding ?? accessDetail.collection.amount_due}
              shipmentCount={accessLedger?.shipmentCount || accessDetail.collection.shipment_ids?.length || 0}
              unloadingKept={accessDetail.collection.unloading_kept ?? accessLedger?.unloadingReceiving}
              note={accessLedger?.note}
            />
          ) : (
            <p className="text-sm text-gray-500">No To-Pay amount mapped for this agent.</p>
          )}
        </div>
      </SettlementModalShell>
    )}
      {deletedInfoShipment ? (
        <DeletedShipmentInfoModal
          shipment={deletedInfoShipment}
          onClose={() => setDeletedInfoShipment(null)}
        />
      ) : null}
      {docActionMessage ? <Loader overlay message={docActionMessage} /> : null}
  </DashboardLayout>
  )
}

export default ViewMLSheet